import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../ui/card";
import { Alert, AlertTitle, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { AlertCircle, Bell, Check, X } from 'lucide-react';
import { collection, query, where, orderBy, getDocs, updateDoc, doc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { LoadingSpinner } from "../ui/loading-spinner";
import { useToast } from "../../hooks/use-toast";
import { Badge } from "../ui/badge";
import { useTheme } from "../ui/theme-provider"; // Import useTheme
import { cn } from "../../lib/utils"; // Ensure cn is imported

const NotificationDisplay = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { theme } = useTheme(); // Get current theme

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    const loadNotifications = async () => {
      setLoading(true);
      try {
        const now = new Date();

        // Query for all active notifications:
        // - Global notifications OR ones targeting this user
        // - Active status
        // - Not expired (no expiresAt or expiresAt > now)
        // - Not dismissed by this user
        const notificationsRef = collection(db, 'notifications');
        const q = query(
          notificationsRef,
          where('isActive', '==', true),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);

        // Client-side filtering for more complex conditions
        const allFetched = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Convert Firestore timestamps to JS Date objects
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          expiresAt: doc.data().expiresAt?.toDate() || null,
        }));

        const activeNotifications = allFetched
          .filter(notification => {
            // Check if expired
            if (notification.expiresAt && notification.expiresAt < now) {
              return false;
            }

            // Check if global or targeted
            if (!notification.global && (!notification.targetUsers || !notification.targetUsers.includes(user.uid))) {
              return false;
            }

            // Check if dismissed by user
            if (notification.dismissedBy && notification.dismissedBy.includes(user.uid)) {
              return false;
            }

            return true;
          });

        setNotifications(activeNotifications);
      } catch (error) {
        console.error("Error loading notifications:", error);
        toast({
          title: "Error",
          description: "Failed to load notifications",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [user?.uid, toast]);

  // No notifications or still loading
  if (loading) {
    return (
      <Card className={cn(
        theme === 'light' 
          ? "bg-white border-gray-200" 
          : "bg-[#1F1F2E] border-purple-500/10"
      )}>
        <CardContent className="p-4 flex justify-center items-center h-24">
          <LoadingSpinner size="small" />
        </CardContent>
      </Card>
    );
  }

  if (notifications.length === 0) {
    return null; // Don't render anything if there are no notifications
  }

  // Helper to get badge text (capitalize level)
  const getBadgeText = (level) => {
    if (!level) return 'Info';
    return level.charAt(0).toUpperCase() + level.slice(1);
  };

  // Get appropriate alert variant based on notification level
  const getAlertVariant = (level) => {
    switch (level) {
      case 'critical':
        return 'destructive';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      case 'info':
      default:
        return 'default';
    }
  };

  // Get appropriate icon based on notification level
  const getAlertIcon = (level) => {
    switch (level) {
      case 'critical':
        return <AlertCircle className="h-4 w-4" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4" />;
      case 'success':
        return <Check className="h-4 w-4" />;
      case 'info':
      default:
        // Make icon white in dark mode but blue in light mode
        return <Bell className={cn(
          "h-4 w-4", 
          theme === 'light' ? "text-blue-600" : "text-white"
        )} />;
    }
  };

  const handleDismiss = async (notification) => {
    if (!user?.uid || !notification?.id) return;
    try {
      console.log('Attempting to dismiss notification:', notification.id, 'by user:', user.uid);
      
      const notificationRef = doc(db, 'notifications', notification.id);
      
      // Use arrayUnion for the most direct operation
      await updateDoc(notificationRef, {
        dismissedBy: arrayUnion(user.uid)
      });
      
      console.log('Successfully dismissed notification');
      
      // Optimistically update UI
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    } catch (error) {
      console.error("Error dismissing notification:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      
      toast({
        title: "Error",
        description: "Failed to dismiss notification",
        variant: "destructive"
      });
    }
  };

  // Show all notifications stacked
  return (
    <div className="space-y-3 mb-4">
      {notifications.map((notification) => (
        <Card key={notification.id} className={cn(
          "overflow-hidden",
          // Add subtle background color based on notification level
          notification.level === 'critical' && theme === 'light' && "bg-red-100", // Changed from bg-red-50 to bg-red-100
          notification.level === 'warning' && theme === 'light' && "bg-yellow-100",
          notification.level === 'success' && theme === 'light' && "bg-green-100",
          notification.level === 'info' && theme === 'light' && "bg-blue-100",
          notification.level === 'critical' && theme === 'dark' && "bg-red-950/30",
          notification.level === 'warning' && theme === 'dark' && "bg-yellow-950/30",
          notification.level === 'success' && theme === 'dark' && "bg-green-950/30",
          notification.level === 'info' && theme === 'dark' && "bg-blue-950/30",
          // Keep default bg as fallback
          !notification.level && (theme === 'light' 
            ? "bg-white border-gray-200"
            : "bg-[#1F1F2E] border-purple-500/20")
        )}>
          <CardContent className="p-0">
            <Alert
              variant={getAlertVariant(notification.level)}
              className="rounded-none border-t-0 border-x-0 border-b-0 py-3 px-4 bg-transparent"
            >
              {/* Update to use similar layout as the GDC/insurance alerts */}
              <div className="w-full flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getAlertIcon(notification.level)}
                  </div>
                  <div className="flex-grow">
                    <AlertTitle className="flex items-center gap-2 mb-1">
                      <span className={cn(
                        "font-semibold",
                        // Make text color blue in light mode, white in dark mode for info
                        notification.level === 'info' && (theme === 'light' ? 'text-blue-700' : 'text-white')
                      )}>
                        {notification.title}
                      </span>
                      <Badge
                        variant={getAlertVariant(notification.level)}
                        className={cn(
                          "capitalize",
                          // Make badge text color blue in light mode
                          notification.level === 'info' && (theme === 'light' ? 'text-white' : 'text-white')
                        )}
                      >
                        {getBadgeText(notification.level)}
                      </Badge>
                    </AlertTitle>
                    <AlertDescription className={cn(
                      "text-sm",
                      // Ensure message text is visible in light mode
                      notification.level === 'info' && theme === 'light' 
                        ? "text-blue-700" 
                        : theme === 'light' ? "text-gray-700" : "text-gray-300"
                    )}>
                      {notification.message}
                    </AlertDescription>
                  </div>
                </div>

                {/* Updated dismiss button to match GDC/insurance alert style */}
                <Button
                  onClick={() => handleDismiss(notification)}
                  className={cn(
                    "ml-2 px-2 py-1 rounded h-8 w-8 flex-shrink-0 flex items-center justify-center",
                    getAlertVariant(notification.level) === 'destructive'
                      ? theme === 'light'
                        ? "bg-red-200 hover:bg-red-300 text-red-800 border border-red-300"
                        : "bg-red-800 hover:bg-red-700 text-white border border-red-500"
                      : getAlertVariant(notification.level) === 'warning'
                        ? theme === 'light'
                          ? "bg-yellow-200 hover:bg-yellow-300 text-yellow-800 border border-yellow-300" 
                          : "bg-yellow-800 hover:bg-yellow-700 text-white border border-yellow-500"
                        : theme === 'light'
                          ? "bg-blue-200 hover:bg-blue-300 text-blue-800 border border-blue-300"
                          : "bg-blue-800 hover:bg-blue-700 text-white border border-blue-500"
                  )}
                  title="Dismiss"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </Alert>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default NotificationDisplay;
